// Enrichment review: provenance-labeled fields, logo preview, and
// alternative-value toggles. stagesFailed renders a soft note — partial
// enrichment is normal, never an error (FR-S13 mirror).
//
// Structured fields (address / socialMediaUrls / additionalContacts) get
// dedicated presentations — never String(object); the formatting is shared
// with the turn-1 confirm list via fieldValueFormat.
import type { EnrichmentStage, ExtractedField, ExtractionLogo } from '@stash/shared-types';
import { previewValue, valueLines } from './fieldValueFormat';

interface EnrichedReviewProps {
  fields: ExtractedField[];
  logo: ExtractionLogo | null;
  stagesFailed: EnrichmentStage[];
  edits: Record<string, unknown>;
  onEdit: (field: string, value: unknown) => void;
}

export function EnrichedReview({ fields, logo, stagesFailed, edits, onEdit }: EnrichedReviewProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 py-4">
      {stagesFailed.length > 0 && (
        <p className="rounded-xl bg-secondary px-3 py-2 text-xs text-secondary-foreground">
          We couldn&rsquo;t reach every source — here&rsquo;s what we found.
        </p>
      )}
      {logo && (
        <img src={logo.url} alt="Business logo" width={64} height={64}
          className="self-center rounded-xl border border-border" />
      )}
      <dl className="flex flex-col gap-2">
        {fields.map((field) => {
          const value = field.field in edits ? edits[field.field] : field.value;
          return (
            <div key={field.field} className="rounded-xl border border-border bg-card px-3 py-2">
              <dt className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                {field.field}
                <span className="rounded-full bg-[var(--stash-blue-light)] px-2 py-0.5 text-[10px] font-semibold text-[var(--stash-blue-dark)]">
                  {field.provenance}
                </span>
              </dt>
              <dd className="m-0 text-sm font-semibold text-foreground">
                {valueLines(field.field, value).map((line) => (
                  <span key={line} className="block break-words">{line}</span>
                ))}
              </dd>
              {(field.alternatives ?? []).map((alternative, index) => (
                <button
                  key={`${previewValue(field.field, alternative.value)}-${index}`}
                  type="button"
                  onClick={() => onEdit(field.field, alternative.value)}
                  className="mt-1 mr-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:bg-[var(--stash-blue-light)]"
                >
                  use: {previewValue(field.field, alternative.value)}
                </button>
              ))}
            </div>
          );
        })}
      </dl>
    </div>
  );
}
